/**
 * 月次経理（FMT）の型の読み書き。
 *
 * 型そのものは Firestore に入れてある（投入は
 * `apps/api/ado-task/scripts/seed-monthly-fmt-items.ts`）。画面は入っているものを
 * 読んで、追加・修正・削除をするだけにする。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の型を API から読み、追加・更新・削除と初回の取り込みを画面へ提供する。
 * @graph-connects fetchMonthlyFmtItems [calls] 型を引く
 * @graph-connects createMonthlyFmtItem [calls] 型を作る
 * @graph-connects updateMonthlyFmtItem [calls] 型を直す
 * @graph-connects deleteMonthlyFmtItem [calls] 型を消す
 */
import { toMessage } from './to-message.js';
import { useCallback, useEffect, useState } from 'react';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import {
  createMonthlyFmtItem,
  deleteMonthlyFmtItem,
  fetchMonthlyFmtItems,
  updateMonthlyFmtItem,
} from './client.js';

/** 画面へ渡すもの */
export interface MonthlyFmtState {
  /** 型の一覧 */
  items: readonly MonthlyFmtTask[];
  /** 読み込み中か */
  loading: boolean;
  /** 失敗したときの理由。無ければ空文字 */
  error: string;
  /**
   * 読み込み自体が失敗した理由。無ければ空文字。
   *
   * 「読めなかった（一覧が信じられない）」と「書けなかった（一覧は読めている）」を
   * 画面が区別するために分けて渡す。前者では取り込みを案内してはいけないが、
   * 後者では続きを取り込ませたい
   */
  loadError: string;
  /** 1 件足す。書けたときだけ true */
  add: (item: MonthlyFmtTask) => Promise<boolean>;
  /** 1 件直す。書けたときだけ true */
  update: (item: MonthlyFmtTask) => Promise<boolean>;
  /** 1 件消す。消せたときだけ true */
  remove: (id: string) => Promise<boolean>;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT の型（月次経理・決算）を読み込み、書き込みのたびに読み直す
 * @graph-connects fetchMonthlyFmtItems [calls] 型を引く
 */
export function useMonthlyFmtItems(
  // 読み書きする FMT の API。決算タスクFMT なら `/closing-fmt-items`
  basePath = '/monthly-fmt-items',
): MonthlyFmtState {
  const [items, setItems] = useState<readonly MonthlyFmtTask[]>([]);
  const [loading, setLoading] = useState(true);
  // 読み込みと書き込みで失敗の理由を分けて持つ。書き込みが失敗したあとは読み直すので、
  // 1 つにまとめると読み直しの成功で「書けなかった」という理由が消えてしまう
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError('');

    fetchMonthlyFmtItems(basePath)
      .then((loaded) => {
        if (alive) {
          setItems(loaded);
        }
      })
      .catch((cause: unknown) => {
        if (alive) {
          setLoadError(toMessage(cause));
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [reloadCount, basePath]);

  /**
   * 書き込みのたびに読み直す。失敗したときも読み直すのが要点で、取り込みが途中で
   * 止まったときに手元を古いままにすると、もう一度押したときに作成済みの先頭から
   * 送り直して必ず詰まる。書けたかどうかは呼び出し元へ返す
   */
  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setWriteError('');
    try {
      await action();
      setReloadCount((count) => count + 1);
      return true;
    } catch (cause: unknown) {
      setWriteError(toMessage(cause));
      setReloadCount((count) => count + 1);
      return false;
    }
  }, []);

  return {
    items,
    loading,
    // 書けなかった理由を優先する。読み直しは成功しても、書けていないことに変わりはない
    error: writeError === '' ? loadError : writeError,
    loadError,
    add: (item) => run(() => createMonthlyFmtItem(item, basePath)),
    update: (item) => run(() => updateMonthlyFmtItem(item, basePath)),
    remove: (id) => run(() => deleteMonthlyFmtItem(id, basePath)),
  };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理と決算タスクFMT の全項目を読み、種別ごとに渡す。タスク詳細で区分（大分類・事業・中分類）の選択肢を FMT の全項目から作るため
 * @graph-connects useMonthlyFmtItems [calls] 月次経理と決算タスクFMT を読む
 */
export function useFmtItemsByKind(): Record<string, readonly MonthlyFmtTask[]> {
  const monthly = useMonthlyFmtItems('/monthly-fmt-items');
  const closing = useMonthlyFmtItems('/closing-fmt-items');
  return { 月次経理: monthly.items, 決算: closing.items };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT から起こしたタスクの元の項目のマニュアルを引く。タスクにマニュアルが写っていないときに代わりに出すため
 * @graph-connects none
 */
export function fmtManualOf(
  task: { kind: string; sourceRef?: string },
  fmtItemsByKind: Readonly<Record<string, readonly MonthlyFmtTask[]>>,
): string | undefined {
  if (task.sourceRef === undefined) {
    return undefined;
  }
  return (fmtItemsByKind[task.kind] ?? []).find((item) => item.id === task.sourceRef)?.manual;
}
